import { useEffect, useRef, useState } from 'react';
import notificationApi from '../services/notification-api';

import type { TNotification } from '@src/shared/types/notification';
import type { ParsedQs } from 'qs';

export const useNotificationsInfinityScroll = ({
  queryParam,
  isOpen,
}: {
  queryParam?: ParsedQs;
  isOpen?: boolean;
}) => {
  const [page, setPage] = useState(1);

  const {
    data,
    isFetching,
    isLoading: isNotificationsLoading,
  } = notificationApi.useFetch({
    ...queryParam,
    page_size: 10,
    page_number: page,
  });
  const isNotificationsEmpty =
    !isNotificationsLoading && data?.results.length === 0;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [notifications, setNotifications] = useState<TNotification[]>(
    data?.results || []
  );

  const strigifyQueryParam = JSON.stringify(JSON.stringify(queryParam));

  // Reset notifications when filters (queryParam) change
  useEffect(() => {
    setPage(1);
    setNotifications([]);
  }, [strigifyQueryParam]);

  // update notifications when new data arrives (infinity scroll)
  useEffect(() => {
    if (data?.results?.length) {
      setNotifications((prev) => {
        const updatedMap = new Map(prev.map((n) => [n.id, n]));
        data.results.forEach((n) => {
          updatedMap.set(n.id, n);
        });
        return Array.from(updatedMap.values());
      });
    }
  }, [data]);

  // Handle scroll pagination
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight >=
          container.scrollHeight - 50 &&
        !isFetching
      ) {
        setPage((prev) => prev + 1);
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isFetching, isOpen]);
  return { notifications, isNotificationsEmpty, containerRef };
};
